import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, makeStyles } from '@material-ui/core';
import Refresh from '@material-ui/icons/Refresh';
import { Flex, Switch } from '@mantine/core';
import FilterNone from '@material-ui/icons/FilterList';
import {
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
} from 'mantine-react-table';
import MenuItem from './MenuItem/MenuItem';
import { useSelector } from 'react-redux';
import MenuItemWithPopover from './MenuItem/MenuItemWithPopover';
import SortSelector from './SortSelector';

const useStyles = makeStyles(() => ({
  buttonContainer: {
    display: 'flex',
    border: `1px solid rgba(0, 168, 255, 0.5)`,
    borderRadius: 8,
    paddingLeft: 4,
    height: 49,
    overflow: 'hidden',
    transition: 'border-color .3s ease, background-color 1s ease',
    backgroundColor: '#ffffff',

    '&:hover': {
      border: `1px solid #00A8FF`,
      backgroundColor: `rgba(0, 168, 255, 0.05)`,

      '.contained': {
        borderRight: `1px solid #00A8FF`,
      },
    },
  },
  contained: {
    marginTop: 14,
    padding: 12,
    color: '#00A8FF !important',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    borderRadius: 5,
    border: '1px solid rgba(0, 168, 255, 0.5)',
    transition: 'border-color .3s ease, background-color .3s ease, color .3s ease',

    '&:hover': {
      color: '#00A8FF !important',
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
  },
  selectMenu: {
    backgroundColor: 'transparent',
    borderTopRightRadius: 5,
    borderBottomRightRadius: 5,
    border: 'none',
    overflow: 'hidden',
    transition: 'border-color .3s ease, background-color .3s ease, color .3s ease',

    '&::before': {
      borderBottom: '0 !important',
      backgroundColor: 'transparent',
    },
    '&::after': {
      borderBottom: '0 !important',
      backgroundColor: 'transparent',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiSelect-iconOpen': {
      transform: 'none',
      backgroundColor: 'transparent',
    },

    '& .MuiSelect-select.Mui-disabled': {
      cursor: 'inherit',
      backgroundColor: 'transparent',
    },
  },
  select: {
    minWidth: 0,
    paddingRight: '18px !important',
    paddingLeft: 18,
    height: '74%',
    backgroundColor: 'transparent',
  },
  icon: {
    color: '#00A8FF',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) !important',
  },
  selectorMenu: {
    padding: 0,
    fontWeight: 400,
    width: 171,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    fontSize: '1rem',
    boxSizing: 'border-box',
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    letterSpacing: '0.00938em',
  },
  '@keyframes rotation': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(359deg)',
    },
  },
  selectMenuContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 8,
  },
  selectMenuTitle: {
    height: 33,
    backgroundColor: '#000',
    color: '#fff',
    fontSize: 14,
    marginBottom: 8,
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
  },
}));

const TopBar = ({
                  reloadData,
                  showFiltersButton,
                  showRefreshTableButton,
                  checked,
                  setFilters,
                  setChecked,
                  table,
                  menuActions,
                  rowVirtualizerInstanceRef,
                  setCanShowShimmer,
                  tableTitle,
                  sorting,
                  setSorting,
                  columns,
                }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { en } = useSelector((state) => state.i18n);
  const { tables: tTables } = en;
  const classes = useStyles();
  return (
    <Flex direction={'row'} align='center' justify='space-between'
          style={{ width: '100%', position: 'relative', gap: 50 }}
    >
      {
        tableTitle
          ? <div style={{ flex: '1 1 auto', overflow: 'hidden' }}>{tableTitle}</div>
          : <></>
      }
      <Flex gap='xs' align='center' justify='flex-end'>
        {
          <Flex gap='xs' align='center' justify='flex-start' style={{
            paddingRight: 12,
            margin: '12px 0px',
            borderRight: '1px solid rgb(217, 217, 217)',
          }}>
            <SortSelector sorting={sorting} setSorting={setSorting} columns={columns} />
          </Flex>
        }
        {
          menuActions && <Flex gap='xs' align='center' justify='flex-start'
                               style={{
                                 paddingRight: 16,
                                 margin: '12px 0px',
                                 borderRight: '1px solid rgb(217, 217, 217)',
                               }}>
            {menuActions}
          </Flex>
        }
        <Flex gap='xs' align='center' justify='flex-end'
              style={{ margin: '12px 0px' }}>
          {
            showRefreshTableButton && reloadData && <MenuItem
              icon={<Refresh />}
              onClick={() => {
                setCanShowShimmer(true);
                if (rowVirtualizerInstanceRef.current) {
                  try {
                    rowVirtualizerInstanceRef.current.scrollToIndex(0);
                  } catch (error) {
                    console.error(error);
                  }
                }
                reloadData(true);
              }
              }
              label={tTables.refreshData}
            />
          }
          {
            showFiltersButton && <>
              <MenuItemWithPopover
                icon={<FilterNone />}
                label={tTables.filterSettings}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                popoverOptions={<>
                  <Switch
                    checked={checked}
                    onChange={(event) => setChecked(event.currentTarget.checked)}
                    label='Show filters'
                  />
                  <Button
                    className={classes.contained}
                    startIcon={<Refresh />}
                    onClick={() => {
                      setFilters(() => []);
                      setAnchorEl(null);
                    }}
                  >
                    {'Reset all filters'}
                  </Button>
                </>}
              />
            </>
          }
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleDensePaddingButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
        </Flex>
      </Flex>
    </Flex>
  );
};

TopBar.propTypes = {
  reloadData: PropTypes.func.isRequired,
  hasRightPadding: PropTypes.bool,
  isLoading: PropTypes.bool,
};

TopBar.defaultProps = {
  hasRightPadding: false,
  isLoading: false,
};

export default TopBar;
